import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../store/authSlice";
import { Package, Clock, LogOut, ChevronRight, Loader2, ShoppingBag, Phone, Mail } from "lucide-react";
import axios from "axios";
import Navbar from "../components/nav/Navbar";
import SiteFooter from "../components/SiteFooter";

const API = process.env.BACKEND_URL;
const USER_API = process.env.REACT_APP_USER_SERVICE_URL;

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function accountAge(createdAt) {
  if (!createdAt) return "—";
  const now = new Date();
  const created = new Date(createdAt);
  const diffMs = now - created;
  const days = Math.floor(diffMs / 86400000);
  if (days < 1) return "Today";
  if (days === 1) return "1 day";
  if (days < 30) return `${days} days`;
  const months = Math.floor(days / 30);
  if (months === 1) return "1 month";
  if (months < 12) return `${months} months`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem > 0 ? `${years}y ${rem}mo` : `${years}y`;
}

function statusColor(status) {
  if (!status) return {};
  const s = status.toLowerCase();
  if (s.includes("delivered") || s.includes("complete") || s.includes("paid"))
    return { background: "rgba(34,197,94,0.1)", color: "#16a34a", border: "1px solid rgba(34,197,94,0.2)" };
  if (s.includes("pending") || s.includes("processing") || s.includes("shipped"))
    return { background: "rgba(106,0,244,0.06)", color: "var(--vb-indigo)", border: "1px solid rgba(106,0,244,0.15)" };
  if (s.includes("cancel") || s.includes("fail") || s.includes("refund"))
    return { background: "hsl(0 72% 51% / 0.08)", color: "hsl(0 72% 51%)", border: "1px solid hsl(0 72% 51% / 0.2)" };
  return { background: "rgba(0,0,0,0.04)", color: "inherit", border: "1px solid var(--border)" };
}

export default function AccountPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, isAuthenticated, accessToken } = useSelector((s) => s.auth);

  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(true);

  // Guard — redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Fetch order history directly from the FastAPI backend.
  // Queries the orders collection by user_id, falling back to email.
  // This is simpler and more reliable than routing through the user_service
  // transaction_history array.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    setTxLoading(true);

    const params = new URLSearchParams();
    if (user.user_id) params.set("user_id", user.user_id);
    else if (user.id)  params.set("user_id", user.id);
    if (user.email)    params.set("email", user.email);

    axios
      .get(`${API}/orders/mine?${params.toString()}`, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      })
      .then((r) => setTransactions(Array.isArray(r.data) ? r.data : r.data?.orders || []))
      .catch(() => setTransactions([]))
      .finally(() => setTxLoading(false));
  }, [isAuthenticated, accessToken, user]);

  const handleLogout = () => {
    dispatch(logoutUser()).then(() => navigate("/"));
  };

  if (!user) return null;

  return (
    <>
      <Navbar />
      <main className="min-h-screen px-4" style={{ paddingTop: "calc(var(--header-h, 80px) + 40px)", paddingBottom: 80 }}>
        <div className="max-w-3xl mx-auto">

          {/* Page header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-display text-3xl md:text-4xl tracking-tight" style={{ lineHeight: 1.1 }}>My Account</h1>
              <p className="font-body text-sm text-muted-foreground mt-1">
                Manage your profile and order history
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border font-body text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              style={{ background: "none", cursor: "pointer" }}
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>

          {/* ── Profile card — avatar left, details right ──────────── */}
          <div className="rounded-3xl border border-border bg-card p-6 md:p-8 mb-6" style={{ boxShadow: "var(--glow-card)" }}>
            <div className="flex flex-col md:flex-row items-start gap-6">

              {/* Avatar column */}
              <div className="flex flex-col items-center flex-shrink-0" style={{ minWidth: 100 }}>
                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center"
                  style={{ background: "var(--grad-primary)", boxShadow: "0 6px 24px rgba(106,0,244,0.25)" }}
                >
                  <span className="font-display text-2xl text-white">
                    {user.first_name?.[0]}{user.last_name?.[0]}
                  </span>
                </div>
                <div className="mt-3 flex items-center gap-1.5">
                  <Clock className="h-3 w-3 text-muted-foreground" />
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {accountAge(user.created_at)}
                  </span>
                </div>
              </div>

              {/* Details column */}
              <div className="flex-1 min-w-0">
                <h2 className="font-body text-xl font-semibold text-foreground">
                  {user.first_name} {user.last_name}
                </h2>

                <div className="mt-4 flex flex-col gap-3">
                  {/* Email */}
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: "var(--vb-off)" }}
                    >
                      <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground leading-none mb-0.5">Email</p>
                      <p className="font-body text-sm text-foreground truncate">{user.email}</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: "var(--vb-off)" }}
                    >
                      <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground leading-none mb-0.5">Phone</p>
                      <p className="font-body text-sm text-foreground">{user.phone || "Not provided"}</p>
                    </div>
                  </div>

                  {/* Member since */}
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: "var(--vb-off)" }}
                    >
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground leading-none mb-0.5">Member Since</p>
                      <p className="font-body text-sm text-foreground">{formatDate(user.created_at)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Order history ──────────────────────────────────────── */}
          <div className="rounded-3xl border border-border bg-card p-6 md:p-8" style={{ boxShadow: "var(--glow-card)" }}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5" style={{ color: "var(--vb-indigo)" }} />
                <h3 className="font-body text-lg font-semibold text-foreground">Order History</h3>
              </div>
              {!txLoading && transactions.length > 0 && (
                <span className="font-mono text-[10px] text-muted-foreground">
                  {transactions.length} order{transactions.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            {txLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-10 w-10 mx-auto text-muted-foreground mb-3" style={{ opacity: 0.35 }} />
                <p className="font-body text-sm text-muted-foreground">No orders yet</p>
                <button
                  onClick={() => navigate("/shop")}
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-body text-sm font-semibold transition-colors"
                  style={{ color: "var(--vb-indigo)", background: "rgba(106,0,244,0.06)", border: "1px solid rgba(106,0,244,0.15)", cursor: "pointer" }}
                >
                  Browse Machines
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {transactions.map((tx, i) => (
                  <div
                    key={tx.order_id || i}
                    className="flex items-center gap-4 px-5 py-4 rounded-2xl border border-border hover:bg-secondary transition-colors cursor-pointer"
                    onClick={() => {
                      if (tx.order_id) navigate(`/portal/${tx.order_id}`);
                    }}
                    style={{ cursor: tx.order_id ? "pointer" : "default" }}
                  >
                    {/* Order info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-body text-sm font-semibold text-foreground truncate">
                          {tx.product_name || tx.order_number || `Order ${(tx.order_id || "").slice(-8)}`}
                        </p>
                        {tx.quantity && tx.quantity > 1 && (
                          <span className="font-mono text-[10px] text-muted-foreground flex-shrink-0">x{tx.quantity}</span>
                        )}
                      </div>
                      <p className="font-body text-xs text-muted-foreground mt-0.5">
                        {formatDate(tx.created_at)}
                        {tx.order_number && <span className="ml-2 font-mono text-[10px]">#{tx.order_number}</span>}
                      </p>
                    </div>

                    {/* Amount */}
                    <div className="text-right flex-shrink-0">
                      <p className="font-mono text-sm font-semibold" style={{ color: "var(--vb-indigo)" }}>
                        ${(tx.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </p>
                    </div>

                    {/* Status badge */}
                    <div
                      className="flex-shrink-0 px-3 py-1 rounded-lg font-mono text-[10px] uppercase tracking-wider font-medium"
                      style={statusColor(tx.status)}
                    >
                      {tx.status || "—"}
                    </div>

                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mobile logout */}
          <button
            onClick={handleLogout}
            className="md:hidden w-full mt-6 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-border font-body text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            style={{ background: "none", cursor: "pointer" }}
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>

        </div>
      </main>
      <SiteFooter />
    </>
  );
}
